<template>
  <main class="pp-page--shopping-cart" :class="cartIsEmpty ? 'empty-cart' : ''">
    <h1 class="title title--desktop"
      v-t="'message.empty_basket'"
      v-if="cartIsEmpty && !errorPresent"/>
    <h1 class="title title--desktop"
      v-t="'message.shopping_cart'"
      v-if="!cartIsEmpty"/>
    <div class="pp-layout-container">
      <div class="pp-preamble">
        <h1 class="title title--mobile"
          v-t="'message.empty_basket'"
          v-if="cartIsEmpty && !errorPresent"/>
        <h1 class="title title--mobile"
          v-t="'message.shopping_cart'"
          v-if="!cartIsEmpty" />
      </div>
      <section class="pp-main-content" v-if="!cartIsEmpty">
        <shopping-cart-product
          v-for="item in cart.aggregatedCart"
          :key="cart.aggregatedCart[item]"
          :productContents="item"
          @confirmDeletion="confirmDeletion"
        />
      </section>
      <aside class="pp-sidebar" v-if="!cartIsEmpty">
        <a :href="getWhatsOnUrl" class="btn btn--secondary btn--whats-on">
          {{ $t("buttons.save_basket_and_continue") }}
        </a>
        <!-- desktop actions in the sidebar -->
        <cart-total/>
        <delivery-options v-if="cart.deliveryOptions.length > 0" 
          :showSelectDeliveryOptionError="showSelectDeliveryOptionError"/>
        <terms-and-conditions
          :termsAndCondState="termsAndCondState"
          :addTermsErrorClass="addTermsErrorClass"
          @toggleTermsAndCondState="toggleTermsAndCondState"
        />
        <newsletter-signup
          :newsletterState="user.newsletterState"
          @toggleNewsletterSignupState="toggleNewsletterSignupState"
        />
        <div class="checkout-cta">
          <fancy-button :disabled="!termsAndCondState || this.cart.activeDeliveryCode == 'BLANK_DELIVERY_OPTION'" @click.native="checkTerms" :icon="['far', 'ticket-alt']">{{ $t("buttons.checkout") }}</fancy-button>
        </div>
      </aside>
      <!-- The mobile actions -->
      <div class="pp-actions"  v-if="!cartIsEmpty">
        <div class="pp-actions__container">
          <delivery-options v-if="cart.deliveryOptions.length > 0" 
            :showSelectDeliveryOptionError="showSelectDeliveryOptionError"/>
          <cart-total/>
          <terms-and-conditions
            :termsAndCondState="termsAndCondState"
            :addTermsErrorClass="addTermsErrorClass"
            @toggleTermsAndCondState="toggleTermsAndCondState"
          />
          <newsletter-signup
            :newsletterState="user.newsletterState"
            @toggleNewsletterSignupState="toggleNewsletterSignupState"
          />
          <fancy-button
            :disabled="!termsAndCondState || this.cart.activeDeliveryCode == 'BLANK_DELIVERY_OPTION'"
            @click.native="checkTerms"
            :icon="['far', 'ticket-alt']">
            {{ $t("buttons.checkout") }}
          </fancy-button>
          <a :href="getWhatsOnUrl" class="btn btn--secondary btn--whats-on">{{ $t("buttons.save_basket_and_continue") }}</a>
        </div>
      </div>
    </div>
    <modal-confirm-deletion @deleteItem="deleteItem" v-if="!cartConfirmationPage" />
  </main>
</template>

<script>
import ShoppingCartProduct from "@/components/shopping-cart/ShoppingCartProduct";
import TermsAndConditions from "@/components/shopping-cart/TermsAndConditions";
import DeliveryOptions from "@/components/shopping-cart/DeliveryOptions";
import CartTotal from "@/components/shopping-cart/CartTotal";
import NewsletterSignup from "@/components/shopping-cart/NewsletterSignup";
import FancyButton from "@/components/generic/FancyButton";
import ModalConfirmDeletion from "@/components/modals/ModalConfirmDeletion";
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: "shopping-cart",
  components: {
    ShoppingCartProduct,
    TermsAndConditions,
    NewsletterSignup,
    DeliveryOptions,
    CartTotal,
    FancyButton,
    ModalConfirmDeletion
  },
  data() {
    return {
      termsAndCondState: false,
      checkoutButtonStateDisabled: true,
      addTermsErrorClass: false,
      itemThatMayBeDeleted: {}
    };
  },
  watch: {
    "$i18n.locale": function() {
      this.initMounted();
    }
  },
  async mounted() {
    await this.initMounted();
  },
  methods: {
    async initMounted() {
      this.updateStage("shopping-cart");
      // We wipe old delivery details to ensure the next page prepopulates
      // correctly from CRM.
      this.updateDeliveryInformation({});
      await this.getCartItems("initialRetrieval");
    },
    toggleNewsletterSignupState() {
      // Saving the state in the store for sending it to the CRM after
      // successful payment.
      this.updateNewsletterState(!this.user.newsletterState);
    },
    checkTerms() {
      if (!this.termsAndCondState) {
        this.addTermsErrorClass = true;
      } else {
        // Any views loaded after this point will need to disable loading on mounted
        this.updateLoading(true);
        this.scrollToTopOfPurchasePath();

        if(this.addressRequired) {
          this.$router.push({ name: "delivery-details", params: { locale: this.$i18n.locale } });
        } else {
          this.$router.push({ name: "payment", params: { locale: this.$i18n.locale } })
        }
      }
    },
    toggleTermsAndCondState() {
      // When the user accepts the Terms, the Checkout button gets enabled,
      // if the checkbox is unchecked, the Checkout gets disabled again.
      this.termsAndCondState = !this.termsAndCondState;
      this.checkoutButtonStateDisabled = !this.checkoutButtonStateDisabled;
      this.addTermsErrorClass = false;
    },
    confirmDeletion(item) {
      this.itemThatMayBeDeleted = item;
      this.$modal.show("modal-confirm-deletion");
    },
    async deleteItem() {
      this.scrollToTopOfPurchasePath();

      let itemToDelete = {
        cartItemId: this.itemThatMayBeDeleted.cartItemId,
        priceClassCode: this.itemThatMayBeDeleted.priceclass.priceClassCode,
      };

      this.removeCartItem({ itemToDelete: itemToDelete, context: "remove"})
    },
    ...mapActions([
      'updateStage',
      'updateNewsletterState',
      'getCartItems',
      'removeCartItem',
      'updateLoading',
      'scrollToTopOfPurchasePath',
      'updateDeliveryInformation',
    ])
  },
  computed: {
    cartConfirmationPage() {
      return this.$route.name == 'cart-confirmation'
    },
    showSelectDeliveryOptionError() {
      if(this.termsAndCondState && this.cart.activeDeliveryCode == 'BLANK_DELIVERY_OPTION') {
        return true
      } else {
        return false
      }
    },
    ...mapGetters(['cartIsEmpty', 'errorPresent', 'addressRequired', 'getWhatsOnUrl']),
    ...mapState(['user', 'cart'])
  },
};
</script>
